import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import { PLATFORM_LOGO } from '../../utils/constants'
import { canSeeSimpleHqNav } from '../../apps/simple-hq/constants'
import AdminViewAsSelect from './AdminViewAsSelect'
import MobileMenu from './MobileMenu'

function getCompanyLogoUrl(logoValue: string | undefined): string | null {
  if (!logoValue) return null
  if (logoValue.startsWith('http')) return logoValue
  if (logoValue.startsWith('logo')) return `/assets/${logoValue}`
  return null
}

const Navbar = () => {
  const { user, isAdmin, companyId, viewAsCompanyId, signOut } = useAuth()
  const { settings, company } = useCompany()
  const companyLogoUrl = getCompanyLogoUrl(settings?.theme_config?.logo)
  const [hasShadow, setHasShadow] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setHasShadow(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleSignOut = async () => {
    console.log('handleSignOut called - button clicked'); // Debug: Verify function is called
    try {
      console.log('Calling signOut()...');
      await signOut()
      console.log('Sign out successful')
    } catch (error: any) {
      console.error('Sign out error:', error)
      alert(`Failed to sign out: ${error.message || 'Unknown error'}`)
    }
  }

  // Dispatch custom events for modals (handled by App component)
  const openAuthModal = () => {
    window.dispatchEvent(new CustomEvent('openAuthModal'))
  }
  const openChangePassword = () => {
    window.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'changePassword' } }))
  }

  const openAdminDashboard = () => {
    window.dispatchEvent(new CustomEvent('openAdminDashboard'))
  }

  // Match EarlySignalsGuard: show link only when user would be allowed access (undefined = enabled)
  const earlySignalsEnabled = settings?.features?.earlySignals !== false
  const showEarlySignals = !!(user && (isAdmin || (companyId && earlySignalsEnabled)))
  const showSimpleHq = canSeeSimpleHqNav(user?.email, companyId, viewAsCompanyId)

  return (
    <>
      <nav
        className={`fixed w-full z-50 bg-white border-b border-gray-100 transition-all duration-300 ${
          hasShadow ? 'shadow-sm' : ''
        }`}
        id="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0 flex items-center gap-3">
              <a href="#" className="flex items-center">
                <img
                  src={PLATFORM_LOGO}
                  alt="The Simple"
                  className="h-10 w-auto"
                />
              </a>
              {companyLogoUrl && (
                <>
                  <span className="h-6 w-px bg-gray-300" aria-hidden />
                  <img
                    src={companyLogoUrl}
                    alt={company?.name || 'Company'}
                    className="h-10 w-auto max-h-10 object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                  />
                </>
              )}
            </div>

            <div className="hidden md:flex space-x-8 items-center">
              <Link to="/" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                Home
              </Link>
              <a href="#about" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                About Us
              </a>
              <a href="#stack" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                The Stack
              </a>
              <a href="#industries" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                Industries
              </a>
              <a href="#faq" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                FAQ
              </a>
              <a href="#contact" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                Contact
              </a>
              <a href="#executive-briefings" className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2">
                Newsletter
              </a>

              {showEarlySignals && (
                <Link
                  to="/early-signals"
                  className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2"
                >
                  Early Signals
                </Link>
              )}
              {showSimpleHq && (
                <Link
                  to="/simple-hq"
                  className="text-sm font-semibold text-gray-900 bg-white border-2 border-teal-600 text-teal-800 rounded-full hover:bg-teal-50 hover:border-teal-700 shadow-md hover:shadow-lg transition-all px-4 py-2"
                >
                  Simple HQ
                </Link>
              )}
              {isAdmin && <AdminViewAsSelect variant="navbar" />}
              {isAdmin && (
                <button
                  onClick={openAdminDashboard}
                  className="text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-shield-halved"></i> Admin
                </button>
              )}

              {!user ? (
                <button
                  onClick={openAuthModal}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 transition-all transform hover:scale-105"
                >
                  Sign In
                </button>
              ) : (
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-900">
                    {user.email.split('@')[0]}
                  </span>
                  <button
                    onClick={openChangePassword}
                    className="text-sm text-gray-600 hover:text-black"
                  >
                    Change password
                  </button>
                  <button
                    onClick={(e) => {
                      console.log('Sign Out button clicked', e);
                      handleSignOut();
                    }}
                    className="text-sm font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 shadow-md hover:shadow-lg transition-all px-4 py-2"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            <MobileMenu
              user={user}
              isAdmin={isAdmin}
              showEarlySignals={!!showEarlySignals}
              showSimpleHq={!!showSimpleHq}
              onSignIn={openAuthModal}
              onSignOut={handleSignOut}
              onAdminClick={openAdminDashboard}
              onChangePassword={openChangePassword}
            />
          </div>
        </div>
      </nav>
    </>
  )
}

export default Navbar
